import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function CouplesRestaurantPickerPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Couples Restaurant Picker — End the 'Where Do You Want to Eat?' Fight"
        description="The #1 restaurant picker for couples. Both partners swipe independently on nearby restaurants — Hangrily only matches you on places you both love. No more dinner arguments. Decide in under 2 minutes. Free, no account needed."
        path="/couples-restaurant-picker"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Couples Restaurant Picker - Date Night Decision App',
            description: 'The restaurant decision app built for couples. Swipe together, match on restaurants you both love.',
            url: 'https://hangrily.app/couples-restaurant-picker'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Couples Restaurant Picker', item: 'https://hangrily.app/couples-restaurant-picker' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How do couples decide where to eat?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'With Hangrily, both partners swipe on restaurants from their own device. The app uses a matching algorithm to find restaurants you both liked. No arguing, no compromising — just a restaurant you\'re both excited about.'
                }
              },
              {
                '@type': 'Question',
                name: 'Do both people need accounts?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes, both partners need free Hangrily accounts. You link them once via email, and after that, starting a couple session is instant — no codes or re-linking needed.'
                }
              },
              {
                '@type': 'Question',
                name: 'What if we have very different food preferences?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'That\'s actually when Hangrily works best! The algorithm finds the overlap between your preferences. Even if you love sushi and your partner loves burgers, there might be a fusion place or a restaurant with diverse options you both swipe right on.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can we use it for deciding on takeout?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely. Hangrily shows all restaurants near your location, whether you\'re dining in or ordering takeout. Just swipe on what looks good, regardless of how you plan to eat.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Couples Restaurant Picker</h1>
          <p className="hero-subtitle">
            "Where do you want to eat?" "I don't care, you pick." "No, not that."
            <br />Sound familiar? We fixed it.
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>The #1 Restaurant Decision App for Couples</h2>
              <p>
                According to surveys, couples argue about where to eat an average of <strong>156 times per year</strong>. 
                That's 3 times a week spent in the frustrating loop of "what sounds good?" followed by 
                endless vetoes and compromises.
              </p>
              <p>
                Hangrily's <strong>Couple Mode</strong> eliminates this entirely. Both partners swipe on 
                restaurants from their own phone — no peeking at each other's choices. When you're both done, 
                Hangrily reveals only the restaurants you <em>both</em> liked. The highest-scoring match wins. 
                Decision made. Argument avoided. Dinner booked.
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Try Couple Mode Free →
              </button>
            </div>
            <div className="column-visual">
              <div className="feature-showcase-box">
                <h3>💑 How Couple Mode Works</h3>
                <ol className="showcase-steps">
                  <li><strong>Link once:</strong> Connect your partner's account via email</li>
                  <li><strong>Start a session:</strong> Either partner can initiate anytime</li>
                  <li><strong>Swipe independently:</strong> Both swipe on the same restaurants</li>
                  <li><strong>Match together:</strong> Only mutual likes advance</li>
                  <li><strong>Eat happy:</strong> Get your shared top pick in 2 minutes</li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Why Couples Love Hangrily</h2>
          <div className="reason-cards">
            <div className="reason-card">
              <div className="reason-icon">🤫</div>
              <h3>Blind Swiping</h3>
              <p>
                Neither partner sees what the other picked until both are done. This eliminates the 
                social pressure of "I'll just agree with whatever you want" — both of you are honest 
                about what you actually want to eat.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">⚖️</div>
              <h3>Equal Say, Every Time</h3>
              <p>
                No more one person always deciding or the other always compromising. Hangrily's algorithm 
                weights both partners' preferences equally. If you both love a place, it rises to the top. 
                If only one of you likes it, it drops.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">⏱️</div>
              <h3>2-Minute Decisions</h3>
              <p>
                The average couple spends 30+ minutes deciding where to eat. With Hangrily, the entire 
                process takes about 2 minutes. That's 28 minutes of your evening saved — every single time.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">📲</div>
              <h3>Hunger Nudges</h3>
              <p>
                Getting hangry? Send your partner a playful "I'm hungry!" nudge directly through the app. 
                They'll get a notification and can jump into a session immediately. No awkward "so... what 
                are we doing for dinner?" texts needed.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">📊</div>
              <h3>Shared History</h3>
              <p>
                Track every restaurant you've decided on together. See your dining patterns, favorite 
                cuisines as a couple, and how often you agree on the first round. It's like a relationship 
                diary, but for food.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🔄</div>
              <h3>Quick Re-Sessions</h3>
              <p>
                Restaurant closed? Changed your mind? Start a new session in seconds. Your partner 
                auto-joins since your accounts are linked — no codes or invites needed.
              </p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Perfect for Date Night</h2>
          <p className="section-subtitle">
            Whether it's a Tuesday dinner or a special anniversary, Hangrily makes the restaurant decision 
            the easiest part of your evening.
          </p>
          <div className="use-case-grid">
            <div className="use-case-card">
              <span className="use-case-icon">🌹</span>
              <h3>Anniversary Dinners</h3>
              <p>Set your price range to $$$$ and let Hangrily find the perfect upscale spot you'll both love.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">🍕</span>
              <h3>Casual Weeknights</h3>
              <p>Quick decision for a low-key dinner. Set preferences and swipe through local favorites in under a minute.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">🌏</span>
              <h3>Trying Something New</h3>
              <p>Expand your palate together. Select "Adventure" mood and discover cuisines you've never tried before.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">🏠</span>
              <h3>Takeout Nights</h3>
              <p>Even ordering in is hard when two people can't agree. Swipe on takeout-friendly spots near you.</p>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How do couples decide where to eat?</h3>
              <p>
                With Hangrily, both partners swipe on restaurants from their own device. The app uses a 
                matching algorithm to find restaurants you both liked. No arguing, no compromising — just 
                a restaurant you're both excited about.
              </p>
            </div>
            <div className="faq-item">
              <h3>Do both people need accounts?</h3>
              <p>
                Yes, both partners need free Hangrily accounts. You link them once via email, and after 
                that, starting a couple session is instant — no codes or re-linking needed.
              </p>
            </div>
            <div className="faq-item">
              <h3>What if we have very different food preferences?</h3>
              <p>
                That's actually when Hangrily works best! The algorithm finds the overlap between your 
                preferences. Even if you love sushi and your partner loves burgers, there might be a 
                fusion place or a restaurant with diverse options you both swipe right on.
              </p>
            </div>
            <div className="faq-item">
              <h3>Can we use it for deciding on takeout?</h3>
              <p>
                Absolutely. Hangrily shows all restaurants near your location, whether you're dining in 
                or ordering takeout. Just swipe on what looks good, regardless of how you plan to eat.
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <Link to="/faq" className="btn btn-secondary btn-lg">See All FAQs →</Link>
          </div>
        </section>

        <section className="cta-banner">
          <h2>End the Dinner Argument Tonight</h2>
          <p>Join thousands of couples who've stopped fighting about food. Swipe, match, eat.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Start Your First Couple Session →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
